import {
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { formatRect } from "components/DocumentMapping/pdfFieldGeometry";
import {
  LINKED_ONLY_OWNERS,
  fieldTypes,
  linkedDetailGroups,
  signerRoles,
} from "./documentFieldOptions";

/**
 * One template field, fully editable in place. Every control that used to live in a separate
 * panel below the list sits in the field's own card, so a template with dozens of fields is
 * scrolled through rather than making the page grow without limit.
 */
const TemplateFieldCard = ({
  field,
  index,
  isSelected,
  onSelect,
  onChange,
  onFieldTypeChange,
  onPrefillKeyChange,
  onDelete,
}) => (
  <Card
    variant="outlined"
    data-field-index={index}
    sx={{ p: 1.5, mb: 1, borderColor: isSelected ? "primary.main" : "divider" }}
    onClick={onSelect}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <TextField
        size="small"
        variant="standard"
        label="Label"
        fullWidth
        value={field.label || ""}
        onChange={(event) => onChange("label", event.target.value)}
      />
      <TextField
        size="small"
        variant="standard"
        label="Name"
        fullWidth
        value={field.name || ""}
        onChange={(event) => onChange("name", event.target.value)}
      />
      <IconButton
        color="error"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
      >
        <DeleteOutline fontSize="small" />
      </IconButton>
    </Stack>

    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <TextField
        select
        size="small"
        label="Field type"
        fullWidth
        value={field.fieldType || "TEXT"}
        disabled={Boolean(field.prefillKey)}
        helperText={field.prefillKey ? "Set automatically from the linked detail" : undefined}
        onChange={(event) => onFieldTypeChange(event.target.value)}
      >
        {fieldTypes.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Ownership"
        fullWidth
        value={field.signerRole || "ADMIN_PRE_SEND"}
        helperText={
          field.prefillKey
            ? "Linked to a detail — pick AUTOFILLED to block sending when it's missing, or CLIENT_IF_NONE to let the client fill it in instead"
            : undefined
        }
        onChange={(event) => onChange("signerRole", event.target.value)}
      >
        {(field.prefillKey ? LINKED_ONLY_OWNERS : signerRoles).map((role) => (
          <MenuItem key={role} value={role}>
            {role}
          </MenuItem>
        ))}
      </TextField>
    </Stack>

    <TextField
      select
      size="small"
      label="Linked client detail"
      fullWidth
      sx={{ mt: 1.5 }}
      value={field.prefillKey || ""}
      onChange={(event) => onPrefillKeyChange(event.target.value)}
    >
      <MenuItem value="">None — client fills this in</MenuItem>
      {linkedDetailGroups.flatMap(({ group, options }) => [
        <ListSubheader key={group}>{group}</ListSubheader>,
        ...options.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        )),
      ])}
    </TextField>

    {field.fieldType === "CHECKBOX" && (
      <TextField
        size="small"
        label="Exclusive group"
        fullWidth
        sx={{ mt: 1.5 }}
        value={field.exclusiveGroup || ""}
        helperText="Give every box of one choice the same group name. Ticking one then clears the rest, the way the original PDF behaves. Leave empty for a checkbox that stands on its own."
        onChange={(event) => onChange("exclusiveGroup", event.target.value)}
      />
    )}

    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <TextField
        size="small"
        label="Show only when"
        fullWidth
        value={field.visibleWhen || ""}
        helperText="e.g. mg.app=Yes, or cc.other count>=2. Blank means always shown."
        onChange={(event) => onChange("visibleWhen", event.target.value)}
      />
      <TextField
        size="small"
        label="Default value"
        fullWidth
        value={field.defaultValue || ""}
        helperText="Pre-filled when the form is created. For a checkbox, its ticked on-state."
        onChange={(event) => onChange("defaultValue", event.target.value)}
      />
    </Stack>

    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(field.required)}
            onChange={(event) => onChange("required", event.target.checked)}
          />
        }
        label="Required"
      />
      <Chip size="small" label={`p${field.pageNumber || 1} · ${formatRect(field)}`} />
    </Stack>
  </Card>
);

export default TemplateFieldCard;
